import React, { useState } from 'react';
import { Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Page, useReport, dateTime, humanize } from './api';
import { Empty, LoadState, Notice, Pagination } from './shared';
type Event = {
  id: string;
  type: string;
  createdAt: string;
  userId?: string;
  person: string;
  billId?: string;
  billTitle?: string;
  error?: string;
  detail?: string;
  source: string;
};
export default function Activity({
  refresh,
  retry,
  onBill,
  onUser,
}: {
  refresh: number;
  retry: () => void;
  onBill: (id: string) => void;
  onUser: (id: string) => void;
}) {
  const [q, setQ] = useState(''),
    [search, setSearch] = useState(''),
    [category, setCategory] = useState('all'),
    [offset, setOffset] = useState(0);
  const { data, loading, error } = useReport<Page<Event>>(
    '/activity',
    {
      q: search,
      category,
      offset,
    },
    refresh
  );
  return (
    <section className="admin-panel">
      <div className="admin-section-heading">
        <div>
          <h2>What’s happening</h2>
          <p>Recorded app, payment and admin actions · newest first · excludes test bills</p>
        </div>
      </div>
      <div className="admin-filters">
        <form
          className="admin-search"
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(q);
            setOffset(0);
          }}
        >
          <Search size={18} />
          <input
            aria-label="Search activity"
            placeholder="Search a person, bill, or action…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <Button variant="ghost" type="submit">
            Search
          </Button>
        </form>
        <select
          aria-label="Activity category"
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
            setOffset(0);
          }}
        >
          <option value="all">All activity</option>
          <option value="errors">Errors</option>
          <option value="payment">Payment activity</option>
          <option value="admin">Admin changes</option>
        </select>
      </div>
      <LoadState loading={loading} error={error} retry={retry} />
      {data && (
        <>
          {data.warnings?.map((w) => (
            <Notice key={w}>{w}</Notice>
          ))}
          {data.items.length ? (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Person</th>
                    <th>Bill</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((event, index) => (
                    <tr key={`${event.id}-${index}`}>
                      <td>
                        <strong>{humanize(event.type)}</strong>
                        {event.detail && <small>{event.detail}</small>}
                        {event.error && <p className="admin-row-error">{event.error}</p>}
                      </td>
                      <td>
                        {event.userId && event.userId !== 'system' ? (
                          <button
                            className="admin-text-link secondary"
                            onClick={() => onUser(event.userId!)}
                          >
                            {event.person}
                          </button>
                        ) : (
                          event.person
                        )}
                      </td>
                      <td>
                        {event.billId ? (
                          <button className="admin-text-link" onClick={() => onBill(event.billId!)}>
                            {event.billTitle || 'View bill'}
                          </button>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>{dateTime(event.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Empty title="No matching activity" detail="Try another search or activity category." />
          )}
          <Pagination
            total={data.total}
            offset={offset}
            next={data.nextOffset}
            onPage={setOffset}
          />
        </>
      )}
    </section>
  );
}
